import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { MessageCircle, Play } from 'lucide-react';
import PlaybackControls from '../components/PlaybackControls';
import { useTranslation } from 'react-i18next';
import { getTrackById } from '../services/trackService';
import { listShares, type TrackShare } from '../services/shareService';
import { getTrackPermissions } from '../utils/trackPermissions';
import { useAuth } from '../contexts/AuthContext';
import { useAudioComments } from '../hooks/useAudioComments';
import AudioWavePlayer from '../components/AudioWavePlayer';
import CassetteLoader from '../components/CassetteLoader';
import { resolveTrackMediaUrl } from '../services/mediaAccess';
import { logError } from '../services/logtail';
import { formatTimeMs } from '../utils/timeUtils';
import BinaryUnavailablePlaceholder from '../components/BinaryUnavailablePlaceholder';

const AudioCommentsPage: React.FC = () => {
  const { t } = useTranslation();
  const { trackId, fileId: fileIdParam } = useParams<{ trackId: string; fileId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const fileId = fileIdParam || searchParams.get('fileId') || '';
  const { user } = useAuth();

  const [trackName, setTrackName] = useState('');
  const [fileName, setFileName] = useState('');
  const [permissions, setPermissions] = useState<{ canAddAudioComments: boolean }>({ canAddAudioComments: false });
  const [binaryBlocked, setBinaryBlocked] = useState(false);
  const [binaryReason, setBinaryReason] = useState<string | undefined>();
  const [requiredProvider, setRequiredProvider] = useState<string | undefined>();
  const [storageProvider, setStorageProvider] = useState<string | undefined>();

  // Audio player state
  const [blobUrl, setBlobUrl] = useState<string>('');
  const [isPaused, setIsPaused] = useState(true);

  // Reply state
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');

  // Add comment state
  const [showAddCommentForm, setShowAddCommentForm] = useState(false);
  const [newCommentText, setNewCommentText] = useState('');
  const [newCommentTimestamp, setNewCommentTimestamp] = useState(0);



  const {
    comments,
    loading,
    userMap,
    createComment,
    addCommentReply,
    resolveComment,
    deleteComment,
    refreshComments,
  } = useAudioComments({
    trackId: trackId || '',
    fileId: fileId || '',
    canComment: permissions.canAddAudioComments,
    canView: !!user, // Only allow viewing if user is authenticated
  });

  // Load track and file info with permissions
  useEffect(() => {
    const loadTrackInfo = async () => {
      if (!trackId || !user) return;

      try {
        const track = await getTrackById(trackId);
        if (track) {
          setTrackName(track.name);
          setBinaryBlocked(track.binaryAccess === false);
          setBinaryReason(track.binaryInactiveReason);
          setRequiredProvider(track.requiredStorageProvider);
          setStorageProvider(track.storageProvider);
          
          // Find the specific audio file
          const audioFile = track.audioFiles?.find(f => f.fileId === fileId);
          if (audioFile) {
            setFileName(audioFile.name);
          }
        }

        // Load track shares to determine permissions
        const shares = await listShares(trackId);
        const userShare = shares.find((share: TrackShare) => share.collaboratorUserId === user.uid);
        
        let userRole = 'viewer';
        let isOwner = false;
        
        if (userShare) {
          userRole = userShare.role;
          isOwner = false;
        } else {
          // Check if user is the track owner
          isOwner = shares.length === 0 || (shares.length > 0 && shares[0].ownerUserId === user.uid);
          if (isOwner) {
            userRole = 'owner';
          }
        }
        
        // Get permissions from role
        const trackPermissions = getTrackPermissions(userRole, isOwner);
        setPermissions({ canAddAudioComments: trackPermissions.canAddAudioComments });
      } catch (error) {
        logError('Failed to load track info', { trackId, error });
      }
    };

    loadTrackInfo();
  }, [trackId, fileId, user]);

  // Load audio file
  useEffect(() => {
    const loadAudioFile = async () => {
      if (!trackId || !fileId || !user || binaryBlocked) {
        return;
      }

      try {
        const access = await resolveTrackMediaUrl(trackId, fileId, 'audio', storageProvider);
        setBlobUrl(access.url);
      } catch (error) {
        console.error('Failed to load audio file:', error);
        logError('Failed to load audio file', { trackId, fileId, error });
        setBlobUrl('');
      }
    };

    loadAudioFile();
  }, [trackId, fileId, user, binaryBlocked, storageProvider, t]);

  // Handle selected comment
  useEffect(() => {
    const selectedCommentId = searchParams.get('selectedComment');
    if (selectedCommentId && comments.length > 0) {
      const comment = comments.find(c => c.threadId === selectedCommentId);
      if (comment) {
        // Seek to comment timestamp in audio player
        if (blobUrl) {
          const audio = document.querySelector('audio');
          if (audio) {
            const time = comment.timestampMs / 1000;
            audio.currentTime = time;
          }
        }
        
        // Scroll to comment with temporary highlighting
        setTimeout(() => {
          const element = document.getElementById(`comment-${selectedCommentId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            // Add temporary highlight
            element.style.backgroundColor = 'var(--info-amber-bg)';
            element.style.borderColor = 'var(--info-amber-border)';
            element.style.boxShadow = '0 0 0 2px var(--info-amber-border)';
            
            // Remove highlight after 3 seconds
            setTimeout(() => {
              element.style.backgroundColor = '';
              element.style.borderColor = '';
              element.style.boxShadow = '';
            }, 3000);
          }
        }, 500);
      }
    }
  }, [searchParams, comments, blobUrl]);


  const getUserDisplay = (authorUserId: string) => {
    if (authorUserId === user?.uid) {
      return t('audioComments.you');
    }
    const userInfo = userMap[authorUserId];
    return userInfo ? userInfo.email : authorUserId;
  };

  // Sort comments: unresolved first, then resolved
  const sortedComments = [...comments].sort((a, b) => {
    if (a.resolved === b.resolved) {
      return a.timestampMs - b.timestampMs;
    }
    return a.resolved ? 1 : -1;
  });

  const unresolvedCount = comments.filter(c => !c.resolved).length;

  // Handler for submitting a reply
  const handleReplySubmit = async (threadId: string) => {
    if (!replyText.trim()) return;
    
    try {
      await addCommentReply(threadId, replyText);
      setReplyText('');
      setReplyingTo(null);
    } catch (error) {
      // Error is handled by the hook
    }
  };

  // Handler for comment marker clicks - update URL and scroll to comment
  const handleCommentClick = (threadId: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('selectedComment', threadId);
      return next;
    });
    
    // Scroll to comment with temporary highlighting
    setTimeout(() => {
      const element = document.getElementById(`comment-${threadId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  // Handler for delete comment with confirmation
  const handleDeleteComment = async (threadId: string) => {
    const confirmed = window.confirm(t('audioComments.confirmDeleteComment'));
    if (confirmed) {
      try {
        await deleteComment(threadId);
      } catch (error) {
        // Error is handled by the hook
      }
    }
  };

  // Handler for adding new comment
  const handleAddComment = async () => {
    if (!newCommentText.trim()) return;
    
    try {
      await createComment(newCommentTimestamp, newCommentText);
      setNewCommentText('');
      setShowAddCommentForm(false);
      setNewCommentTimestamp(0);
      // Refresh comments to show the new one
      await refreshComments();
    } catch (error) {
      // Error is handled by the hook
    }
  };

  // Get current audio time for new comments
  const getCurrentAudioTime = () => {
    const audio = document.querySelector('audio');
    if (audio && audio.duration && !isNaN(audio.currentTime)) {
      return Math.floor(audio.currentTime * 1000);
    }
    return 0;
  };

  // Handler for opening add comment form
  const handleOpenAddComment = () => {
    const currentTime = getCurrentAudioTime();
    setNewCommentTimestamp(currentTime);
    setShowAddCommentForm(true);
  };

  // Handler for updating timestamp when user seeks to different position
  // The AudioWavePlayer now passes the timestamp directly via onSeek
  const handleSeekToPosition = (timestampMs: number) => {
    if (showAddCommentForm) {
      setNewCommentTimestamp(timestampMs);
    }
  };

  return (
    <div className="w-full">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--color-text)' }}>
          {trackName || t('audioComments.comments')}
        </h1>
        <div className="flex items-center gap-2">
          <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
            {fileName}
          </p>
          {permissions.canAddAudioComments && (
            <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--info-blue-bg)', color: 'var(--info-blue-text)' }}>
              {t('share.editor')}
            </span>
          )}
        </div>
      </div>

      {/* Audio Player */}
      <div className="mb-6 rounded-lg border p-4" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
        <div>
          {binaryBlocked ? (
            <BinaryUnavailablePlaceholder
              reason={binaryReason}
              requiredProvider={requiredProvider}
              viewerRole="collaborator"
            />
          ) : blobUrl ? (
            <div>
              {/* Playback Controls */}
              <PlaybackControls
                isPaused={isPaused}
                onPlay={() => setIsPaused(false)}
                onPause={() => setIsPaused(true)}
                onStop={() => {
                  setIsPaused(true);
                  // Reset audio to beginning
                  const audio = document.querySelector('audio');
                  if (audio) {
                    audio.currentTime = 0;
                    audio.pause();
                  }
                }}
                size="md"
                className="mb-4"
              />
              
              {/* Waveform Player */}
               <AudioWavePlayer
                 trackId={trackId || ''}
                 fileId={fileId || ''}
                 fileName={fileName}
                 blobUrl={blobUrl}
                 isPaused={isPaused}
                 onPause={() => setIsPaused(true)}
                 onContinue={() => setIsPaused(false)}
                 onStop={() => setIsPaused(true)}
                 canComment={permissions.canAddAudioComments}
                 canView={true}
                 onCommentAdded={refreshComments}
                 onCommentClick={handleCommentClick}
                 showCommentTiles={false}
                 onSeek={handleSeekToPosition}
                 storageProvider={storageProvider}
               />
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <CassetteLoader size={60} variant="inline" />
                </div>
                <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
                  {t('audioComments.loadingAudio')}...
                </p>
                <button 
                  onClick={() => {
                    // Retry loading audio
                    const loadAudioFile = async () => {
                      if (!trackId || !fileId || !user) return;
                      try {
                        const access = await resolveTrackMediaUrl(trackId, fileId, 'audio', storageProvider);
                        setBlobUrl(access.url);
                      } catch (error) {
                        console.error('Retry failed:', error);
                      }
                    };
                    loadAudioFile();
                  }}
                  className="mt-2 text-xs px-3 py-1 rounded transition-colors"
                  style={{ backgroundColor: 'var(--color-hover)', color: 'var(--color-text)' }}
                >
                   {t('audioComments.retry')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Comments List */}
      <div className="mt-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <CassetteLoader size={60} variant="inline" />
            <p className="text-sm mt-4" style={{ color: 'var(--color-muted)' }}>
              {t('audioComments.loading')}...
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Add Comment Section - Always show for editors */}
            {permissions.canAddAudioComments && (
              <div className="mb-6">
                {!showAddCommentForm ? (
                  <button
                    onClick={handleOpenAddComment}
                    className="w-full py-3 px-4 rounded-lg border-2 border-dashed transition-colors flex items-center justify-center gap-2"
                    style={{ 
                      borderColor: 'var(--brand-from)', 
                      color: 'var(--brand-from)',
                      backgroundColor: 'var(--color-surface)'
                    }}
                  >
                    <MessageCircle className="w-4 h-4" />
                    <span className="font-medium">{t('audioComments.addComment')}</span>
                  </button>
                ) : (
                  <div className="p-3 sm:p-4 rounded-lg border" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-sm font-semibold" style={{ color: 'var(--brand-from)' }}>
                        @{formatTimeMs(newCommentTimestamp)}
                      </span>
                      <span className="text-xs hidden sm:inline" style={{ color: 'var(--color-muted)' }}>
                        {t('audioComments.addCommentAtCurrentTime')}
                      </span>
                    </div>
                    <div className="space-y-3">
                      <textarea
                        value={newCommentText}
                        onChange={(e) => setNewCommentText(e.target.value)}
                        placeholder={t('audioComments.addCommentPlaceholder')}
                        className="w-full px-3 py-2 text-sm rounded border resize-none"
                        style={{
                          backgroundColor: 'var(--color-bg)',
                          borderColor: 'var(--color-border)',
                          color: 'var(--color-text)',
                          minHeight: '80px'
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                            e.preventDefault();
                            handleAddComment();
                          }
                        }}
                        autoFocus
                      />
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => {
                            setShowAddCommentForm(false);
                            setNewCommentText('');
                            setNewCommentTimestamp(0);
                          }}
                          className="px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ backgroundColor: 'var(--color-hover)', color: 'var(--color-text)' }}
                        >
                          {t('buttons.cancel')}
                        </button>
                        <button
                          onClick={handleAddComment}
                          className="px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ backgroundColor: 'var(--brand-from)', color: 'white' }}
                          disabled={!newCommentText.trim()}
                        >
                          {t('audioComments.addComment')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* No Comments State */}
            {comments.length === 0 ? (
              <div className="text-center py-12">
                <MessageCircle className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--color-muted)' }} />
                <h3 className="text-lg font-medium mb-2" style={{ color: 'var(--color-text)' }}>
                  {t('audioComments.noComments')}
                </h3>
              </div>
            ) : (
              <>
                {/* Unresolved Comments */}
            {unresolvedCount > 0 && (
              <div className="mb-6">
                <h2 className="text-sm font-semibold mb-3" style={{ color: 'var(--color-text)' }}>
                  {t('audioComments.unresolvedComments')} ({unresolvedCount})
                </h2>
                <div className="space-y-3">
                  {sortedComments.filter(c => !c.resolved).map((comment) => (
                      <div
                        key={comment.threadId}
                        id={`comment-${comment.threadId}`}
                        className="rounded-lg border p-4 transition-all"
                        style={{ 
                          backgroundColor: 'var(--color-surface)', 
                          borderColor: 'var(--color-border)'
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs font-semibold" style={{ color: 'var(--brand-from)' }}>
                                @{formatTimeMs(comment.timestampMs)}
                              </span>
                              <button
                                onClick={async () => {
                                  // Seek to 2 seconds before comment (or 0 if less than 2 seconds)
                                  const seekTime = Math.max(0, (comment.timestampMs / 1000) - 2);
                                  const audio = document.querySelector('audio') as HTMLAudioElement;
                                  if (audio) {
                                    audio.currentTime = seekTime;
                                    // Start playing
                                    try {
                                      await audio.play();
                                      setIsPaused(false);
                                    } catch (error) {
                                      console.error('Failed to play audio:', error);
                                    }
                                  }
                                }}
                                className="px-2 py-1 text-xs font-medium rounded transition-colors flex items-center gap-1 hover:opacity-90"
                                style={{ 
                                  backgroundColor: 'var(--brand-from)', 
                                  color: 'white' 
                                }}
                                title={t('audioComments.playThisComment')}
                              >
                                <Play className="w-3 h-3" />
                                {t('audioComments.play')}
                              </button>
                            </div>
                            <p className="text-sm mb-2" style={{ color: 'var(--color-text)' }}>
                              {comment.text}
                            </p>
                            <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--color-muted)' }}>
                              <span>{getUserDisplay(comment.authorUserId)}</span>
                              <span>•</span>
                              <span>{new Date(comment.createdAt).toLocaleDateString()}</span>
                            </div>
                          </div>
                          {permissions.canAddAudioComments && (
                            <div className="flex items-center gap-1 ml-3">
                              <button
                                onClick={() => resolveComment(comment.threadId, true)}
                                className="px-3 py-1.5 text-xs font-medium rounded transition-colors"
                                style={{ backgroundColor: 'var(--info-green-bg)', color: 'var(--info-green-text)' }}
                                title={t('audioComments.resolve')}
                              >
                                {t('audioComments.resolve')}
                              </button>
                              <button
                                onClick={() => handleDeleteComment(comment.threadId)}
                                className="px-3 py-1.5 text-xs font-medium rounded transition-colors"
                                style={{ backgroundColor: 'var(--info-red-bg)', color: 'var(--info-red-text)' }}
                                title={t('buttons.delete')}
                              >
                                {t('buttons.delete')}
                              </button>
                            </div>
                          )}
                        </div>
                        
                        {/* Replies */}
                        {comment.replies && comment.replies.length > 0 && (
                          <div className="mt-4 pt-4 border-t space-y-3" style={{ borderColor: 'var(--color-border)' }}>
                            {comment.replies.map((reply) => (
                              <div key={reply.replyId} className="flex items-start gap-3 pl-4">
                                <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: 'var(--color-hover)' }}>
                                  <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
                                    {getUserDisplay(reply.authorUserId).charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-medium" style={{ color: 'var(--color-text)' }}>
                                      {getUserDisplay(reply.authorUserId)}
                                    </span>
                                    <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
                                      {new Date(reply.createdAt).toLocaleDateString()}
                                    </span>
                                  </div>
                                  <p className="text-sm" style={{ color: 'var(--color-text)' }}>
                                    {reply.text}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Reply Form */}
                        {permissions.canAddAudioComments && (
                          <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
                            {replyingTo === comment.threadId ? (
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={replyText}
                                  onChange={(e) => setReplyText(e.target.value)}
                                  placeholder={t('audioComments.addReply')}
                                  className="flex-1 px-3 py-2 text-sm rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--btn-primary-ring)] focus:border-[var(--btn-primary-ring)]"
                                  style={{ 
                                    backgroundColor: 'var(--color-bg)', 
                                    borderColor: 'var(--color-border)',
                                    color: 'var(--color-text)'
                                  }}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      handleReplySubmit(comment.threadId);
                                    }
                                  }}
                                  autoFocus
                                />
                                <button
                                  onClick={() => handleReplySubmit(comment.threadId)}
                                  className="px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                                  style={{ backgroundColor: 'var(--brand-from)', color: 'white' }}
                                  disabled={!replyText.trim()}
                                >
                                  {t('audioComments.reply')}
                                </button>
                                <button
                                  onClick={() => {
                                    setReplyingTo(null);
                                    setReplyText('');
                                  }}
                                  className="px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 hover:opacity-90"
                                  style={{ backgroundColor: 'var(--color-hover)', color: 'var(--color-text)' }}
                                >
                                  {t('buttons.cancel')}
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setReplyingTo(comment.threadId)}
                                className="text-sm px-4 py-2 rounded-lg font-medium transition-all duration-200 hover:opacity-90"
                                style={{ backgroundColor: 'var(--color-hover)', color: 'var(--color-text)' }}
                              >
                                {t('audioComments.addReply')}
                              </button>
                            )}
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Resolved Comments */}
            {comments.filter(c => c.resolved).length > 0 && (
              <div>
                <h2 className="text-sm font-semibold mb-3" style={{ color: 'var(--color-muted)' }}>
                  {t('audioComments.resolvedComments')} ({comments.filter(c => c.resolved).length})
                </h2>
                <div className="space-y-3">
                  {sortedComments.filter(c => c.resolved).map((comment) => (
                    <div
                      key={comment.threadId}
                      id={`comment-${comment.threadId}`}
                      className="rounded-lg border p-4 transition-all opacity-60"
                      style={{ 
                        backgroundColor: 'var(--color-hover)', 
                        borderColor: 'var(--color-border)'
                      }}
                    >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs font-semibold" style={{ color: 'var(--color-muted)' }}>
                                @{formatTimeMs(comment.timestampMs)}
                              </span>
                              <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--info-green-bg)', color: 'var(--info-green-text)' }}>
                                {t('audioComments.resolved')}
                              </span>
                              <button
                                onClick={async () => {
                                  // Seek to 2 seconds before comment (or 0 if less than 2 seconds)
                                  const seekTime = Math.max(0, (comment.timestampMs / 1000) - 2);
                                  const audio = document.querySelector('audio') as HTMLAudioElement;
                                  if (audio) {
                                    audio.currentTime = seekTime;
                                    // Start playing
                                    try {
                                      await audio.play();
                                      setIsPaused(false);
                                    } catch (error) {
                                      console.error('Failed to play audio:', error);
                                    }
                                  }
                                }}
                                className="px-2 py-1 text-xs font-medium rounded transition-colors flex items-center gap-1 hover:opacity-90"
                                style={{ 
                                  backgroundColor: 'var(--brand-from)', 
                                  color: 'white' 
                                }}
                                title={t('audioComments.playThisComment')}
                              >
                                <Play className="w-3 h-3" />
                                {t('audioComments.play')}
                              </button>
                            </div>
                            <p className="text-sm mb-2" style={{ color: 'var(--color-muted)' }}>
                              {comment.text}
                            </p>
                            <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--color-muted)' }}>
                              <span>{getUserDisplay(comment.authorUserId)}</span>
                              <span>•</span>
                              <span>{new Date(comment.createdAt).toLocaleDateString()}</span>
                            </div>
                          </div>
                          {permissions.canAddAudioComments && (
                            <div className="flex items-center gap-1 ml-3">
                              <button
                                onClick={() => resolveComment(comment.threadId, false)}
                                className="px-3 py-1.5 text-xs font-medium rounded transition-colors"
                                style={{ backgroundColor: 'var(--color-hover)', color: 'var(--color-text)' }}
                                title={t('audioComments.unresolve')}
                              >
                                {t('audioComments.unresolve')}
                              </button>
                              <button
                                onClick={() => handleDeleteComment(comment.threadId)}
                                className="px-3 py-1.5 text-xs font-medium rounded transition-colors"
                                style={{ backgroundColor: 'var(--info-red-bg)', color: 'var(--info-red-text)' }}
                                title={t('buttons.delete')}
                              >
                                {t('buttons.delete')}
                              </button>
                          </div>
                        )}
                      </div>
                      
                      {/* Replies */}
                      {comment.replies && comment.replies.length > 0 && (
                        <div className="mt-4 pt-4 border-t space-y-3" style={{ borderColor: 'var(--color-border)' }}>
                          {comment.replies.map((reply) => (
                            <div key={reply.replyId} className="flex items-start gap-3 pl-4 opacity-60">
                              <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: 'var(--color-hover)' }}>
                                <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
                                  {getUserDisplay(reply.authorUserId).charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs font-medium" style={{ color: 'var(--color-muted)' }}>
                                    {getUserDisplay(reply.authorUserId)}
                                  </span>
                                  <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
                                    {new Date(reply.createdAt).toLocaleDateString()}
                                  </span>
                                </div>
                                <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
                                  {reply.text}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Reply Form for Resolved Comments */}
                      {permissions.canAddAudioComments && (
                        <div className="mt-4 pt-4 border-t opacity-100" style={{ borderColor: 'var(--color-border)' }}>
                          {replyingTo === comment.threadId ? (
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                placeholder={t('audioComments.addReply')}
                                className="flex-1 px-3 py-2 text-sm rounded border"
                                style={{
                                  backgroundColor: 'var(--color-bg)',
                                  borderColor: 'var(--color-border)',
                                  color: 'var(--color-text)'
                                }}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    handleReplySubmit(comment.threadId);
                                  }
                                }}
                                autoFocus
                              />
                              <button
                                onClick={() => handleReplySubmit(comment.threadId)}
                                className="px-3 py-2 text-sm font-medium rounded transition-colors"
                                style={{ backgroundColor: 'var(--brand-from)', color: 'white' }}
                              >
                                {t('audioComments.reply')}
                              </button>
                              <button
                                onClick={() => {
                                  setReplyingTo(null);
                                  setReplyText('');
                                }}
                                className="px-3 py-2 text-sm font-medium rounded transition-colors"
                                style={{ backgroundColor: 'var(--color-hover)', color: 'var(--color-text)' }}
                              >
                                {t('buttons.cancel')}
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setReplyingTo(comment.threadId)}
                              className="text-sm px-3 py-1.5 rounded transition-colors"
                              style={{ backgroundColor: 'var(--color-hover)', color: 'var(--color-text)' }}
                            >
                              {t('audioComments.addReply')}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    ))}
                </div>
              </div>
            )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioCommentsPage;